import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, ArrowRight } from "lucide-react";
import { Reveal, Tag, H2, GoldBtn, GhostBtn, BrushHighlight } from "@/components/sections/shared";
import FoldText from "@/components/FoldText/FoldText";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";

const spots = [
  "Denial language that may need a closer look",
  "Low settlement offers",
  "Delay tactics",
  "Missing explanations",
  "Confusing policy terms",
  "Potential bad-faith warning signs",
  "Situations where professional help may be worth considering",
];

export default function Detector() {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();
  const navigate = useNavigate();

  return (
    <section id="detector" data-testid="detector-section" className="relative bg-[#121214] py-24 md:py-32 overflow-hidden">
      <GlobalSpotlight
        gridRef={gridRef}
        disableAnimations={isMobile}
        glowColor="230, 57, 70"
        spotlightRadius={280}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(600px circle at 85% 20%, rgba(230,57,70,0.07), transparent 60%)" }}
      />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-14 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Tag testId="detector-tag">Private Case Room · File / 001</Tag>
          <H2>
            <FoldText
              text="Your insurance company has experts."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
            />{" "}
            <FoldText
              text="Now you do too."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="#9D4EDD"
            />
          </H2>
          <p className="mt-6 text-[#A1A1AA] font-light leading-relaxed">
            Meet Jack D. Hapsburg, an insurance industry veteran backed by AI-powered claim
            intelligence. Jack helps you understand your claim, spot potential red flags, and
            decide what to do next before you feel forced to hire a lawyer.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <GoldBtn testId="detector-primary-cta" onClick={() => navigate("/rip-off-detector")}>
              TAKE THE <BrushHighlight>FREE</BrushHighlight> RIP-OFF DETECTOR
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </GoldBtn>
            <GhostBtn testId="detector-secondary-cta" onClick={() => window.AIWidget?.ask()}>
              ASK JACK
            </GhostBtn>
          </div>
          <div
            data-testid="detector-proof-strip"
            className="mt-10 pt-5 border-t border-white/10 flex gap-10 sm:gap-14"
          >
            {[
              ["30+", "YEARS INSIDE INSURANCE"],
              ["AI", "CLAIM INTELLIGENCE"],
              ["01", "CLEAR NEXT MOVE"],
            ].map(([big, small]) => (
              <div key={small}>
                <div className="font-display text-2xl text-[#9D4EDD]">{big}</div>
                <div className="mt-1 font-mono-file text-[9px] tracking-[0.2em] text-[#71717A]">
                  {small}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#71717A] font-light">
            Built by an insurance professional with over 30 years of industry experience.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div ref={gridRef} className="bento-section">
            <ParticleCard
              testId="detector-console"
              disableAnimations={isMobile}
              glowColor="230, 57, 70"
              className="magic-bento-card magic-bento-card--border-glow relative bg-[#0A0A0C] border border-white/10 p-8"
              overflowContent={
                <>
                  <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[#E63946]/50" />
                  <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[#E63946]/50" />
                </>
              }
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <span className="font-mono-file text-[10px] tracking-[0.25em] text-[#E63946]">
                  RED FLAG SCAN · ACTIVE
                </span>
                <span className="status-dot w-2 h-2 rounded-full bg-[#E63946]" />
              </div>
              <p className="mt-5 font-mono-file text-[10px] tracking-[0.2em] text-[#71717A]">
                WHAT IT HELPS YOU SPOT
              </p>
              <ul className="mt-4 space-y-3.5">
                {spots.map((s, i) => (
                  <li key={s} data-testid={`detector-spot-${i}`} className="group flex items-start gap-3.5">
                    <Flag size={14} className="mt-1 shrink-0 text-[#E63946]/60 group-hover:text-[#E63946] transition-colors duration-200" />
                    <span className="text-sm text-white/80 font-light leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </ParticleCard>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
